import { FC, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import { CONSTANTS } from "../../../utils/CONSTS";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MenuBook as MenuBookIcon,
} from "@mui/icons-material";
import MainTitle from "../../components/mainTitle";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import { useInstituteContext } from "../../../context/institute.context";
import {
  useSubjectsHook,
  useDeleteSubjectHook,
} from "../../../hooks/useAcademicHook";
import { TSubject } from "../../../apis/academic/academic.interfaces";
import { logger } from "../../../utils/common";
import ManageSubjectDialog from "./manageSubjectDialog";

const AcademicsSubjectsView: FC = () => {
  const { selectedInstitute } = useInstituteContext();
  const { data: subjects, isLoading, error } = useSubjectsHook();
  const deleteMutation = useDeleteSubjectHook();
  const { control, watch } = useForm({ defaultValues: { search: "" } });
  const searchQuery = watch("search");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [subjectToEdit, setSubjectToEdit] = useState<TSubject | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState<TSubject | null>(null);

  const filtered = useMemo(() => {
    if (!subjects) return [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return subjects;
    return subjects.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.code.toLowerCase().includes(q) ||
        (s.description?.toLowerCase().includes(q) ?? false),
    );
  }, [subjects, searchQuery]);

  const handleDeleteConfirm = async () => {
    if (!toDelete) return;
    try {
      await deleteMutation.mutateAsync(toDelete.id);
      logger.show_success("Subject removed.");
      setDeleteOpen(false);
      setToDelete(null);
    } catch (e) {
      logger.show_error(
        e instanceof Error ? e.message : "Could not delete subject.",
      );
    }
  };

  if (!selectedInstitute) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.academics} />
        <Box p={3} data-testid="academics-subjects-view">
          <MainTitle title="Subjects" />
          <Alert severity="info">
            Please select an institute from the dashboard to manage subjects.
          </Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      <DocumentTitle title={CONSTANTS.title.academics} />

      <Grid container spacing={3} data-testid="academics-subjects-view">
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            sx={{ mb: 1 }}
          >
            <MainTitle title="Subject catalog" />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setSubjectToEdit(null);
                setDialogOpen(true);
              }}
              data-testid="add-subject-button"
            >
              Add subject
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Institute-wide subjects (shared catalog). Link them to classes from
            the class hub.
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <ControlledTextField
                control={control as never}
                name="search"
                id="subject-search"
                label="Search"
                isGridItem={false}
                fullWidth
                placeholder="Search by name, code, or description..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                inputProps={{ "data-testid": "subject-search-input" }}
              />
            </CardContent>
          </Card>
        </Grid>

        {isLoading ? (
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          </Grid>
        ) : error ? (
          <Grid item xs={12}>
            <Alert severity="error">Failed to load subjects.</Alert>
          </Grid>
        ) : filtered.length === 0 ? (
          <Grid item xs={12}>
            <Card data-testid="subjects-empty">
              <CardContent>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={2}
                  py={4}
                >
                  <MenuBookIcon
                    sx={{ fontSize: 64, color: "text.secondary" }}
                  />
                  <Typography variant="h6" color="text.secondary">
                    {subjects?.length === 0
                      ? "No subjects yet"
                      : "No matches"}
                  </Typography>
                  {subjects?.length === 0 && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setSubjectToEdit(null);
                        setDialogOpen(true);
                      }}
                    >
                      Add subject
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table data-testid="subjects-table">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Elective</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((s) => (
                    <TableRow key={s.id} hover data-testid={`subject-row-${s.id}`}>
                      <TableCell>
                        <Typography fontWeight="medium">{s.name}</Typography>
                        {s.description ? (
                          <Typography variant="body2" color="text.secondary">
                            {s.description}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{s.code}</TableCell>
                      <TableCell>{s.subjectType ?? "—"}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={s.isElective ? "Yes" : "No"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setSubjectToEdit(s);
                            setDialogOpen(true);
                          }}
                          data-testid={`edit-subject-${s.id}`}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setToDelete(s);
                            setDeleteOpen(true);
                          }}
                          data-testid={`delete-subject-${s.id}`}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}
      </Grid>

      <ManageSubjectDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSubjectToEdit(null);
        }}
        subject={subjectToEdit}
      />

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete subject</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove subject &quot;{toDelete?.name}&quot; from the catalog? Classes
            that still reference it may fail until reassigned.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={deleteMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AcademicsSubjectsView;
